// src/pages/SiteDetails.js - AVEC GESTION DES ERREURS D'ABONNEMENT
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import MapViewer from "../components/MapViewer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const SiteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [site, setSite] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Charger les détails du site
  useEffect(() => {
    const fetchSiteDetails = async () => {
      if (!userInfo || !id) return;
      
      setLoading(true);
      setError("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        // Récupérer les détails du site
        const siteResponse = await axios.get(
          `${API_BASE_URL}/sites/get.php?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (siteResponse.data?.success) {
          setSite(siteResponse.data.site);
          
          // Récupérer les agents affectés à ce site via le nouvel endpoint
          try {
            const agentsResponse = await axios.get(
              `${API_BASE_URL}/affectations/agents-by-site.php?id_site=${id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            
            if (agentsResponse.data?.success) {
              setAgents(agentsResponse.data.agents_affectes || []);
            } else {
              console.warn("⚠️ Impossible de charger les agents affectés:", agentsResponse.data?.message);
              setAgents([]);
            }
          } catch (agentsErr) {
            console.warn("⚠️ Erreur chargement agents affectés:", agentsErr.message);
            setAgents([]);
          }
        } else {
          setError(siteResponse.data?.message || "Site non trouvé");
        }
      } catch (err) {
        console.error("Erreur:", err);
        
        if (err.response?.status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        } else if (err.response?.status === 404) {
          setError("Site non trouvé.");
          setTimeout(() => navigate("/sites"), 3000);
        } else if (!err.response) {
          setError("Serveur indisponible.");
        } else {
          setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSiteDetails();
  }, [id, userInfo, navigate]);

  // Vérifier si l'utilisateur est gestionnaire
  const isGestionnaire = userInfo && String(userInfo.profile).trim() === "3";
  const isControleur = userInfo && String(userInfo.profile).trim() === "2";
  const isDeleted = site?.supprimer === 1;

  // Fonction pour changer le statut du site
  const handleChangeStatus = async (newStatus) => {
    if (!site || !window.confirm(`Êtes-vous sûr de vouloir ${newStatus === 1 ? 'activer' : 'désactiver'} ce site ?`)) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/sites/change_status.php`,
        { id_site: site.id_site, statut_site: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg(`Site ${newStatus === 1 ? 'activé' : 'désactivé'} avec succès.`);
        // Recharger les données
        const refreshResponse = await axios.get(
          `${API_BASE_URL}/sites/get.php?id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (refreshResponse.data?.success) {
          setSite(refreshResponse.data.site);
        }
      } else {
        setError(response.data?.message || "Erreur lors du changement de statut");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      // 🔍 Vérifier si c'est une erreur d'abonnement
      if (err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        // Message avec bouton de souscription
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
      } else {
        setError(err.response?.data?.message || "Erreur technique");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour supprimer le site
  const handleDelete = async () => {
    if (!site || !window.confirm(`Êtes-vous sûr de vouloir supprimer le site "${site.nom_site}" ? Cette action est réversible.`)) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/sites/delete.php`,
        { id_site: site.id_site },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Site supprimé avec succès.");
        setTimeout(() => navigate("/sites"), 2000);
      } else {
        setError(response.data?.message || "Erreur lors de la suppression");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      // 🔍 Vérifier si c'est une erreur d'abonnement
      if (err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        // Message avec bouton de souscription
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
      } else {
        setError(err.response?.data?.message || "Erreur technique");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour restaurer le site
  const handleRestore = async () => {
    if (!site || !window.confirm(`Êtes-vous sûr de vouloir restaurer le site "${site.nom_site}" ?`)) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/sites/restore.php`,
        { id_site: site.id_site },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Site restauré avec succès.");
        // Recharger les données
        const refreshResponse = await axios.get(
          `${API_BASE_URL}/sites/get.php?id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (refreshResponse.data?.success) {
          setSite(refreshResponse.data.site);
        }
      } else {
        setError(response.data?.message || "Erreur lors de la restauration");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setError(err.response?.data?.message || "Erreur technique");
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour retirer un agent du site
  const handleRemoveAgent = async (id_agent) => {
    if (!window.confirm("Êtes-vous sûr de vouloir retirer cet agent de ce site ?")) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      const response = await axios.delete(
        `${API_BASE_URL}/affectations/delete.php`,
        {
          data: { id_site: parseInt(id), id_agent: parseInt(id_agent) },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Agent retiré du site avec succès !");
        // Recharger les données des agents
        try {
          const agentsResponse = await axios.get(
            `${API_BASE_URL}/affectations/agents-by-site.php?id_site=${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          
          if (agentsResponse.data?.success) {
            setAgents(agentsResponse.data.agents_affectes || []);
          }
        } catch (refreshErr) {
          console.warn("⚠️ Erreur rechargement agents:", refreshErr.message);
        }
      } else {
        setError(response.data?.message || "Erreur lors du retrait de l'agent");
      }
    } catch (err) {
      console.error("❌ Erreur retrait agent:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 404) {
        setError(err.response.data?.message || "Affectation non trouvée");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date avec heure
  const formatDateTime = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour le statut
  const getStatutInfo = (statut) => {
    switch (statut) {
      case 1: return { text: "Actif", className: "badge bg-success", icon: "bi-check-circle" };
      case 0: return { text: "Inactif", className: "badge bg-secondary", icon: "bi-x-circle" };
      default: return { text: "Inconnu", className: "badge bg-light text-dark", icon: "bi-question-circle" };
    }
  };

  if (loading || !userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1>Détails du site</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/sites">Sites</Link>
              </li>
              <li className="breadcrumb-item active">Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: '#0B3C5D' }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-2">Chargement des détails...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !site) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1>Détails du site</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/sites">Sites</Link>
              </li>
              <li className="breadcrumb-item active">Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card">
            <div className="card-body">
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error || "Site non trouvé"}
              </div>
              <div className="text-center mt-3">
                <button 
                  className="btn"
                  onClick={() => navigate("/sites")}
                  style={{
                    color: '#0B3C5D',
                    borderColor: '#0B3C5D',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#0B3C5D';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#0B3C5D';
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la liste
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const statutInfo = getStatutInfo(site.statut_site);

  return (
    <Layout>
      <div className="pagetitle">
        <h1>Détails du site</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/sites">Sites</Link>
            </li>
            <li className="breadcrumb-item active">
              {site.nom_site}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* Messages */}
        {successMsg && (
          <div className="alert alert-success mb-3 alert-dismissible fade show">
            <i className="bi bi-check-circle me-2"></i>
            {successMsg}
            <button type="button" className="btn-close" onClick={() => setSuccessMsg("")}></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mb-3 alert-dismissible fade show" style={{ whiteSpace: "pre-line" }}>
            <div className="d-flex align-items-start">
              <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div className="flex-grow-1">{error}</div>
              <button type="button" className="btn-close" onClick={() => setError("")}></button>
            </div>
          </div>
        )}

        <div className="row">
          {/* Colonne gauche: Profil + Actions */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-body">
                <div className="text-center mb-4">
                  {/* Avatar */}
                  <div className="mb-3">
                    <div 
                      className="rounded-circle d-inline-flex align-items-center justify-content-center text-white"
                      style={{ 
                        width: "100px", 
                        height: "100px",
                        backgroundColor: '#28a745',
                        fontSize: '2.5rem'
                      }}
                    >
                      <i className="bi bi-building"></i>
                    </div>
                  </div>
                  
                  <h4>{site.nom_site}</h4>
                  
                  
                  <div>
                    <span className={statutInfo.className}>
                      <i className={`bi ${statutInfo.icon} me-1`}></i>
                      {statutInfo.text}
                    </span>
                    {isDeleted && (
                      <span className="badge bg-danger ms-2">
                        <i className="bi bi-trash me-1"></i> Supprimé
                      </span>
                    )}
                  </div>
                </div>

                {/* SECTION ACTIONS */}
                <hr />
                <h6>Actions</h6>
                
                <div className="d-flex flex-wrap gap-2 justify-content-center align-items-center mb-3">
                  {/* Bouton MODIFIER */}
                  {isGestionnaire && !isDeleted && (
                    <div className="tooltip-container">
                      <button 
                        className="btn btn-primary btn-icon"
                        onClick={() => navigate(`/sites/edit/${site.id_site}`)}
                        disabled={actionLoading}
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-pencil-fill"></i>
                        )}
                      </button>
                      <div className="tooltip-text">Modifier le site</div>
                    </div>
                  )}
                  
                  {/* Bouton ACTIVER/DÉSACTIVER */}
                  {isGestionnaire && !isDeleted && (
                    <div className="tooltip-container">
                      <button 
                        className={`btn ${site.statut_site === 1 ? 'btn-warning' : 'btn-success'} btn-icon`}
                        onClick={() => handleChangeStatus(site.statut_site === 1 ? 0 : 1)}
                        disabled={actionLoading}
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className={`bi ${site.statut_site === 1 ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
                        )}
                      </button>
                      <div className="tooltip-text">{site.statut_site === 1 ? "Désactiver le site" : "Activer le site"}</div>
                    </div>
                  )}
                  
                  {/* Bouton GESTION AFFECTATIONS */}
                  {isGestionnaire && agents.length > 0 && (
                    <div className="tooltip-container">
                      <button 
                        className="btn btn-info btn-icon"
                        onClick={() => navigate(`/affectations/site/${site.id_site}`)}
                        disabled={actionLoading}
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-people-fill"></i>
                        )}
                      </button>
                      <div className="tooltip-text">Gérer les agents affectés</div>
                    </div>
                  )}
                  
                  {/* Bouton SUPPRIMER/RESTAURER */}
                  {isGestionnaire && (
                    <div className="tooltip-container">
                      <button 
                        className={`btn ${isDeleted ? 'btn-success' : 'btn-danger'} btn-icon`}
                        onClick={isDeleted ? handleRestore : handleDelete}
                        disabled={actionLoading}
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : isDeleted ? (
                          <i className="bi bi-arrow-clockwise"></i>
                        ) : (
                          <i className="bi bi-trash-fill"></i>
                        )}
                      </button>
                      <div className="tooltip-text">{isDeleted ? "Restaurer le site" : "Supprimer le site"}</div>
                    </div>
                  )}
                </div>

                {/* Info si pas d'actions */}
                {!isGestionnaire && (
                  <div className="alert alert-info p-2 text-center mb-3">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      {isControleur 
                        ? "Vous êtes en mode consultation seule." 
                        : "Seuls les gestionnaires peuvent modifier les sites."}
                    </small>
                  </div>
                )}
                
                {/* Statistiques */}
                <div className="mt-4">
                  <h6>Statistiques</h6>
                  <div className="row text-center">
                    <div className="col-6 mb-3">
                      <div className="card border-primary">
                        <div className="card-body p-2">
                          <i className="bi bi-people display-6 text-primary"></i>
                          <h5 className="mt-2">{agents.length}</h5>
                          <p className="text-muted mb-0 small">Agents</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-6 mb-3">
                      <div className="card border-success">
                        <div className="card-body p-2">
                          <i className="bi bi-geo-alt display-6 text-success"></i>
                          <h5 className="mt-2">
                            {site.latitude_site && site.longitude_site ? "✓" : "✗"}
                          </h5>
                          <p className="text-muted mb-0 small">GPS</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bouton RETOUR */}
                <div className="d-grid mt-3">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => navigate("/sites")}
                    disabled={actionLoading}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour à la liste
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite: Informations détaillées */}
          <div className="col-lg-8">
            {/* Informations générales */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Informations générales
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%"><strong>Nom du site:</strong></td>
                            <td>{site.nom_site}</td>
                          </tr>
                          <tr>
                            <td><strong>Client:</strong></td>
                            <td>
                              <Link 
                                to={`/clients/details/${site.id_client}`}
                                className="text-decoration-none"
                                style={{ color: '#0B3C5D' }}
                              >
                                <i className="bi bi-person me-1"></i>
                                {site.nom_client}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Créé par:</strong></td>
                            <td>{site.nom_user || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%"><strong>Créé le:</strong></td>
                            <td>{formatDateTime(site.created_at)}</td>
                          </tr>
                          <tr>
                            <td><strong>Modifié le:</strong></td>
                            <td>{formatDateTime(site.updated_at) || "-"}</td>
                          </tr>
                          {isDeleted && (
                            <tr>
                              <td><strong>Supprimé le:</strong></td>
                              <td className="text-danger">{formatDateTime(site.date_supprimer_site)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Localisation */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-geo-alt me-2"></i>
                  Localisation
                </h5>

                <div className="row">
                  <div className="col-md-12 mb-3">
                    <div className="border rounded p-3 bg-light">
                      <div className="d-flex align-items-start">
                        <i className="bi bi-geo-alt-fill me-3 text-primary fs-4"></i>
                        <div>
                          <h6 className="mb-1">Adresse</h6>
                          <p className="mb-0">{site.addresse_site || "-"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-12">
                    <div className="border rounded p-3 bg-light">
                      <h6 className="mb-3">Coordonnées GPS</h6>
                      {site.latitude_site && site.longitude_site ? (
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label small text-muted">Latitude</label>
                            <div className="fw-bold">{site.latitude_site.toFixed(6)}</div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label small text-muted">Longitude</label>
                            <div className="fw-bold">{site.longitude_site.toFixed(6)}</div>
                          </div>
                          <div className="col-12">
                            <button
                              onClick={() => setShowMapModal(true)}
                              className="btn btn-sm w-100"
                              style={{
                                color: '#0B3C5D',
                                borderColor: '#0B3C5D',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#0B3C5D';
                                e.target.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#0B3C5D';
                              }}
                            >
                              <i className="bi bi-map me-1"></i>
                              Voir sur la carte
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-warning p-2 mb-0">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Coordonnées GPS non définies.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Agents affectés (seulement pour gestionnaires) */}
            {isGestionnaire && (
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-people me-2"></i>
                      Agents affectés ({agents.length})
                    </h5>
                    
                    {agents.length > 0 && (
                      <Link
                        to={`/affectations/site/${site.id_site}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-list-check me-1"></i>
                        Voir la gestion complète
                      </Link>
                    )}
                  </div>
                  
                  {agents.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bi bi-people display-6 text-muted"></i>
                      <p className="mt-2">Aucun agent affecté à ce site</p>
                      <button
                        onClick={() => navigate(`/affectations/create?site_id=${site.id_site}`)}
                        className="btn btn-sm"
                        style={{
                          backgroundColor: '#0B3C5D',
                          borderColor: '#0B3C5D',
                          color: 'white'
                        }}
                      >
                        <i className="bi bi-plus-lg me-1"></i> Affecter un agent
                      </button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Nom</th>
                            <th>Matricule</th>
                            <th>Téléphone</th>
                            <th>Statut</th>
                            <th>Compte utilisateur</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agents.map((agent) => (
                            <tr key={agent.id_agent}>
                              <td>
                                <strong>{agent.prenom_agent} {agent.nom_agent}</strong>
                                {agent.email_user && (
                                  <div className="small text-muted">
                                    <i className="bi bi-envelope me-1"></i>
                                    {agent.email_user}
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-secondary">
                                  {agent.matricule_agent || '-'}
                                </span>
                              </td>
                              <td>{agent.tel_agent || '-'}</td>
                              <td>
                                <span className={`badge ${agent.status_agent === 1 ? 'bg-success' : 'bg-secondary'}`}>
                                  {agent.status_agent === 1 ? 'Actif' : 'Inactif'}
                                </span>
                              </td>
                              <td>
                                {agent.has_user_account ? (
                                  <span className="badge bg-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Lié
                                  </span>
                                ) : (
                                  <span className="badge bg-warning">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    Non lié
                                  </span>
                                )}
                              </td>
                              <td>
                                <div className="btn-group" role="group">
                                  {agent.id_user ? (
                                    <Link
                                      to={`/users/details/${agent.id_user}`}
                                      className="btn btn-sm btn-outline-primary me-1"
                                      target="_blank"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </Link>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-secondary me-1"
                                      disabled
                                      title="Cet agent n'est pas lié à un utilisateur"
                                    >
                                      <i className="bi bi-eye-slash"></i>
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveAgent(agent.id_agent)}
                                    disabled={actionLoading}
                                    title="Retirer l'affectation"
                                  >
                                    {actionLoading ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : (
                                      <i className="bi bi-x-circle"></i>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="d-flex justify-content-between mt-3">
                        <div>
                          <Link
                            to={`/affectations/site/${site.id_site}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bi bi-list-check me-1"></i>
                            Gestion complète des affectations
                          </Link>
                        </div>
                        <button
                          onClick={() => navigate(`/affectations/create?site_id=${site.id_site}`)}
                          className="btn btn-sm"
                          style={{
                            backgroundColor: '#0B3C5D',
                            borderColor: '#0B3C5D',
                            color: 'white'
                          }}
                        >
                          <i className="bi bi-plus-lg me-1"></i> Affecter un autre agent
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Modal de carte */}
      <MapViewer
        show={showMapModal}
        onHide={() => setShowMapModal(false)}
        latitude={site?.latitude_site}
        longitude={site?.longitude_site}
        siteName={site?.nom_site}
        siteAddress={site?.addresse_site}
        siteStatus={site?.statut_site}
      />

      {/* Styles CSS */}
      <style>{`
        .tooltip-container {
          cursor: pointer;
          position: relative;
          display: inline-block;
        }
        
        .tooltip-container:hover .tooltip-text {
          display: block !important;
        }
        
        .tooltip-container .tooltip-text {
          display: none;
          position: absolute;
          z-index: 1000;
          background-color: #212529;
          color: white;
          padding: 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          white-space: nowrap;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 5px;
        }
        
        .tooltip-container .tooltip-text::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-width: 5px;
          border-style: solid;
          border-color: #212529 transparent transparent transparent;
        }
        
        .btn-icon:hover {
          transform: scale(1.1);
          transition: transform 0.2s;
        }
        
        .btn-icon:active {
          transform: scale(0.95);
        }
      `}</style>
    </Layout>
  );
};

export default SiteDetails;